import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { DashboardLayout } from "../components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Building2,
  Upload,
  Globe,
  Image,
  X,
  CheckCircle2,
  CreditCard,
  Receipt,
  CalendarDays,
  Sparkles,
  ArrowUpRight,
  Download,
  ExternalLink,
  User,
} from "lucide-react";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import FullScreenLoader from "../components/FullScreenLoader";
import AdminDiscountPanel from "../components/AdminDiscountPannel";
import { DiscountProvider } from "../contexts/DiscountContext";
import { useLanguage } from "../contexts/LanguageContext";

const REGISTER_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/register";
const USER_UPDATE_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/userUpdate";
const INVOICES_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/user/invoices";

const CLOUD_NAME = "dcfotquxt";
const UPLOAD_PRESET = "flsdzoc2";
const CLIENT_SHARED_SECRET = process.env.REACT_APP_JWT_SECRET || "122Codeman!";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "de", label: "Deutsch" },
  { value: "pt", label: "Português" },
  { value: "it", label: "Italiano" },
  { value: "nl", label: "Nederlands" },
  { value: "ja", label: "日本語" },
  { value: "zh", label: "中文" },
  { value: "ko", label: "한국어" },
  { value: "ar", label: "العربية" },
];

const defaultSubscription = {
  tier: "Core",
  startDate: "2025-09-09",
  monthsRemaining: 6,
  assessmentsThisMonth: 2,
  monthsActive: 6,
};

const tierDetails = {
  Core: {
    price: "$750/mo",
    quarterly: "$2,250/qtr",
    assessments: "1 assessment/month (3/quarter)",
  },
  Pro: {
    price: "$500/mo",
    quarterly: "$4,500/qtr",
    assessments: "3 assessments/month",
  },
  Enterprise: {
    price: "Custom",
    quarterly: "Custom",
    assessments: "Unlimited assessments",
  },
};

const invoices = [
  {
    id: "INV-2026-Q1",
    date: "Mar 1, 2026",
    status: "upcoming",
  },
  { id: "INV-2025-Q4", date: "Dec 1, 2025", status: "paid" },
  { id: "INV-2025-Q3", date: "Sep 1, 2025", status: "paid" },
];

const getPlanOption = (data) =>
  data?.planOption ||
  data?.user?.planOption ||
  data?.data?.planOption ||
  data?.subscription?.planOption ||
  null;

const getPlanName = (planOption) => {
  if (!planOption) return defaultSubscription.tier;
  if (typeof planOption === "string") return planOption;
  return (
    planOption.name ||
    planOption.planName ||
    planOption.title ||
    planOption.label ||
    planOption.tier ||
    defaultSubscription.tier
  );
};

const normalizeTier = (planName) => {
  const matchingTier = Object.keys(tierDetails).find(
    (tier) => tier.toLowerCase() === String(planName).toLowerCase(),
  );
  return matchingTier || planName || defaultSubscription.tier;
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return null;
  if (typeof value === "string") return value;
  return `$${Number(value).toLocaleString()}`;
};

const omitPassword = (value = {}) => {
  const sanitized = { ...value };
  delete sanitized.password;
  return sanitized;
};

const SettingsNewV1 = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [companyName, setCompanyName] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [language, setLanguage] = useState("en");
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState("");
  const [billingInfo, setBillingInfo] = useState({});
  const [accountFullName, setAccountFullName] = useState("");
  const [accountPhone, setAccountPhone] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [accountConfirmPassword, setAccountConfirmPassword] = useState("");
  const [accountSuccessOpen, setAccountSuccessOpen] = useState(false);
  const [upcomingInvoices, setUpcomingInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const registerData = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const authToken = registerData?.authToken;
  const subscriptionDetails = useMemo(() => {
    const planOption = getPlanOption(registerData);
    const tier = normalizeTier(getPlanName(planOption));
    const planData =
      planOption && typeof planOption === "object" ? planOption : {};
    const details = tierDetails[tier] || {
      price: formatCurrency(planData.price || planData.monthlyPrice) || "—",
      quarterly:
        formatCurrency(planData.quarterly || planData.quarterlyPrice) || "—",
      assessments:
        planData.assessments ||
        planData.assessmentLimit ||
        t("Assessment limit unavailable"),
    };

    return {
      tier,
      details,
      monthsRemaining:
        planData.monthsRemaining ??
        registerData?.monthsRemaining ??
        registerData?.subscription?.monthsRemaining ??
        defaultSubscription.monthsRemaining,
      assessmentsThisMonth:
        planData.assessmentsThisMonth ??
        registerData?.assessmentsThisMonth ??
        registerData?.subscription?.assessmentsThisMonth ??
        defaultSubscription.assessmentsThisMonth,
      monthsActive:
        planData.monthsActive ??
        registerData?.monthsActive ??
        registerData?.subscription?.monthsActive ??
        defaultSubscription.monthsActive,
    };
  }, [registerData, t]);

  const userId = useMemo(() => {
    if (!registerData) return null;
    return (
      registerData?.userId ||
      registerData?._id ||
      registerData?.user?._id ||
      registerData?.data?.userId ||
      null
    );
  }, [registerData]);

  const accountEmail = useMemo(
    () =>
      registerData?.email ||
      registerData?.user?.email ||
      registerData?.data?.email ||
      registerData?.company?.email ||
      "",
    [registerData],
  );

  const subscriptionStatus = useMemo(() => {
    if (!registerData) return null;
    return (
      registerData?.subscription?.status ||
      registerData?.subscriptionStatus ||
      registerData?.status ||
      (registerData?.subscription?.paid ? "paid" : null) ||
      null
    );
  }, [registerData]);

  const [localSubscriptionStatus, setLocalSubscriptionStatus] =
    useState(subscriptionStatus);

  useEffect(() => {
    setLocalSubscriptionStatus(subscriptionStatus);
  }, [subscriptionStatus]);

  const isAdminUser = useMemo(() => {
    let role = "";
    try {
      role =
        localStorage.getItem("role") ||
        registerData?.role ||
        registerData?.user?.role ||
        registerData?.data?.role ||
        "";
    } catch {
      role = "";
    }
    return role?.toLowerCase() === "admin";
  }, [registerData]);

  useEffect(() => {
    const existingName =
      registerData?.companyName ||
      registerData?.company?.companyName ||
      registerData?.data?.companyName ||
      "";
    const existingLogo =
      registerData?.companyLogo ||
      registerData?.company?.companyLogo ||
      registerData?.data?.companyLogo ||
      "";
    if (existingName) setCompanyName(existingName);
    if (existingLogo) setLogoPreview(existingLogo);
  }, [registerData]);

  useEffect(() => {
    const existingFullName =
      registerData?.fullName ||
      registerData?.user?.fullName ||
      registerData?.data?.fullName ||
      "";
    const existingPhone =
      registerData?.phone ||
      registerData?.user?.phone ||
      registerData?.data?.phone ||
      "";
    setAccountFullName(existingFullName);
    setAccountPhone(existingPhone);
  }, [registerData]);

  useEffect(() => {
    const name =
      registerData?.fullName ||
      registerData?.user?.fullName ||
      registerData?.company?.contact ||
      "";
    const email =
      registerData?.email ||
      registerData?.user?.email ||
      registerData?.company?.email ||
      "";
    const planOption = getPlanOption(registerData);
    const plan = subscriptionDetails.tier;
    const nextBilling =
      registerData?.nextBilling ||
      registerData?.billingDate ||
      registerData?.subscription?.nextBilling ||
      registerData?.subscription?.billingDate ||
      defaultSubscription.startDate ||
      "";
    const planData =
      planOption && typeof planOption === "object" ? planOption : {};
    const amount =
      formatCurrency(
        planData.amount ||
          planData.quarterly ||
          planData.quarterlyPrice ||
          planData.price ||
          planData.monthlyPrice,
      ) ||
      subscriptionDetails.details.quarterly ||
      "—";
    setBillingInfo({
      name: name || "N/A",
      email: email || "N/A",
      nextBilling: nextBilling || "—",
      amount,
      plan,
    });
  }, [registerData, subscriptionDetails]);

  useEffect(() => {
    if (!userId) return;

    const fetchInvoices = async () => {
      setInvoiceLoading(true);
      try {
        const res = await fetch(INVOICES_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify({ userId }),
        });
        const data = await res.json().catch(() => ({}));
        if (
          data?.message?.toLowerCase().includes("unauthorized") ||
          data?.message?.toLowerCase().includes("401")
        ) {
          navigate("/ ");
          return;
        }
        if (!res.ok) {
          throw new Error(data?.message || t("Unable to load invoices."));
        }
        setUpcomingInvoices(data);
      } catch (err) {
        console.error("invoice fetch error", err);
        if (
          err?.message?.toLowerCase().includes("unauthorized") ||
          err?.message?.toLowerCase().includes("401")
        ) {
          navigate("/login");
          return;
        }
        toast({
          title: t("Invoice load failed"),
          description: err?.message || t("Could not load upcoming invoices."),
          variant: "destructive",
        });
      } finally {
        setInvoiceLoading(false);
      }
    };

    fetchInvoices();
  }, [authToken, navigate, t, userId]);

  const loadCloudinaryWidget = useCallback(() => {
    if (typeof window === "undefined") return Promise.reject();
    if (window.cloudinary?.createUploadWidget) {
      return Promise.resolve(window.cloudinary);
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://widget.cloudinary.com/v2.0/global/all.js";
      script.async = true;
      script.onload = () => {
        if (window.cloudinary?.createUploadWidget) {
          resolve(window.cloudinary);
        } else {
          reject(new Error(t("Cloudinary widget failed to load.")));
        }
      };
      script.onerror = () =>
        reject(new Error(t("Cloudinary widget failed to load.")));
      document.body.appendChild(script);
    });
  }, [t]);

  const handleUploadLogo = useCallback(async () => {
    setError("");
    if (!userId) {
      setError(t("Missing user id. Please sign in again."));
      return;
    }
    try {
      setUploadingLogo(true);
      const cloudinary = await loadCloudinaryWidget();
      const widget = cloudinary.createUploadWidget(
        {
          cloudName: CLOUD_NAME,
          uploadPreset: UPLOAD_PRESET,
          sources: ["local", "url"],
          multiple: false,
          resourceType: "image",
          maxFiles: 1,
          folder: `BizVantage/${userId}/Companys`,
        },
        (uploadError, result) => {
          if (uploadError) {
            setUploadingLogo(false);
            toast({
              title: t("Upload failed"),
              description: uploadError.message || t("Could not upload logo."),
              variant: "destructive",
            });
            return;
          }
          if (result?.event === "success" && result?.info) {
            setLogoPreview(result.info.secure_url);
            setLogoFile(null);
            toast({
              title: t("Logo uploaded"),
              description: t("Your company logo has been saved."),
            });
            setUploadingLogo(false);
          }
          if (result?.event === "close") {
            setUploadingLogo(false);
          }
        },
      );
      widget?.open();
    } catch (e) {
      setUploadingLogo(false);
      setError(e.message || t("Unable to open upload widget."));
    }
  }, [loadCloudinaryWidget, t, userId]);

  const { tier, details, monthsRemaining, assessmentsThisMonth, monthsActive } =
    subscriptionDetails;
  const showUpgrade =
    tier === "Core" && monthsActive >= 6 && assessmentsThisMonth > 1;

  const formatInvoiceDate = (value) => {
    if (!value) return "—";
    const date =
      typeof value === "number" ? new Date(value * 1000) : new Date(value);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatInvoiceAmount = (value, currency = "USD") => {
    if (value == null || value === "") return "—";
    const amount = Number(value);
    if (Number.isNaN(amount)) return String(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const subscriptionInvoices = upcomingInvoices.length
    ? upcomingInvoices.map((invoice) => ({
        id:
          invoice.number ||
          invoice.invoice_number ||
          invoice.id ||
          invoice._id ||
          "Invoice",
        date:
          invoice.due_date ||
          invoice.next_payment_attempt ||
          invoice.period_end ||
          invoice.date ||
          "",
        status:
          invoice.status || (invoice.paid ? "paid" : "upcoming") || "upcoming",
        amount:
          invoice.amount_due ||
          invoice.total ||
          invoice.amount_paid ||
          invoice.amount ||
          invoice.price ||
          invoice.quarterly ||
          null,
        currency: invoice.currency || invoice.currency_code || "USD",
        description:
          invoice.description ||
          invoice.statement_descriptor ||
          invoice.lines?.data?.[0]?.description ||
          t("Quarterly invoice"),
        invoiceUrl: invoice.invoice_pdf || invoice.hosted_invoice_url || "",
      }))
    : invoices.map((invoice) => ({
        ...invoice,
        amount: invoice.amount || details.quarterly?.replace("/qtr", "") || "—",
        currency: "USD",
        description: invoice.description || t("Quarterly invoice"),
        invoiceUrl: "",
      }));

  const handleLogoUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) {
      // open cloudinary widget instead
      handleUploadLogo();
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({
        title: t("Invalid file"),
        description: t("Please upload an image file."),
        variant: "destructive",
      });
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = () => {
    toast({
      title: t("Settings saved"),
      description: t("Your preferences have been updated."),
    });
  };

  const handleSaveAccount = async () => {
    setError("");
    const trimmedFullName = accountFullName.trim();
    const trimmedPhone = accountPhone.trim();
    const trimmedPassword = accountPassword.trim();
    const trimmedCompanyName = companyName.trim();

    if (!trimmedFullName) {
      setError(t("Full name is required."));
      toast({
        title: t("Full name required"),
        description: t("Enter your full name before saving account settings."),
        variant: "destructive",
      });
      return;
    }

    if (!trimmedPhone) {
      setError(t("Phone number is required."));
      toast({
        title: t("Phone number required"),
        description: t("Enter your phone number before saving account settings."),
        variant: "destructive",
      });
      return;
    }

    if (!accountEmail) {
      setError(t("Email is required."));
      toast({
        title: t("Email missing"),
        description: t("Please sign in again before updating account settings."),
        variant: "destructive",
      });
      return;
    }

    if (accountConfirmPassword.trim() && !trimmedPassword) {
      setError(t("New password is required."));
      toast({
        title: t("New password required"),
        description: t("Enter a new password before confirming it."),
        variant: "destructive",
      });
      return;
    }

    if (trimmedPassword && trimmedPassword !== accountConfirmPassword.trim()) {
      setError(t("Passwords do not match."));
      toast({
        title: t("Passwords do not match"),
        description: t("Confirm your new password before saving."),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        email: accountEmail,
        fullName: trimmedFullName,
        phone: trimmedPhone,
        companyName: trimmedCompanyName,
        companyLogo: logoPreview,
        ...(trimmedPassword
          ? {
              password: CryptoJS.AES.encrypt(
                trimmedPassword,
                CLIENT_SHARED_SECRET,
              ).toString(),
            }
          : {}),
      };
      const res = await fetch(USER_UPDATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { authorization: authToken } : {}),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        const existingUser = registerData?.user || {};
        const existingData = registerData?.data || {};
        const existingCompany = registerData?.company || {};
        const responseData = omitPassword(data);
        const responseUser = omitPassword(responseData?.user);
        const responseNestedData = omitPassword(responseData?.data);
        const responseCompany = omitPassword(responseData?.company);
        const merged = {
          ...(registerData || {}),
          ...responseData,
          email: accountEmail,
          fullName: trimmedFullName,
          phone: trimmedPhone,
          companyName: trimmedCompanyName,
          companyLogo: logoPreview,
          ...(registerData?.user || responseData?.user
            ? {
                user: {
                  ...existingUser,
                  ...responseUser,
                  email: accountEmail,
                  fullName: trimmedFullName,
                  phone: trimmedPhone,
                },
              }
            : {}),
          ...(registerData?.data || responseData?.data
            ? {
                data: {
                  ...existingData,
                  ...responseNestedData,
                  email: accountEmail,
                  fullName: trimmedFullName,
                  phone: trimmedPhone,
                  companyName: trimmedCompanyName,
                  companyLogo: logoPreview,
                },
              }
            : {}),
          ...(registerData?.company || responseData?.company
            ? {
                company: {
                  ...existingCompany,
                  ...responseCompany,
                  email: accountEmail,
                  companyName: trimmedCompanyName,
                  companyLogo: logoPreview,
                },
              }
            : {}),
        };
        localStorage.setItem("bv.registerResponse", JSON.stringify(merged));
        setBillingInfo((prev) => ({ ...prev, name: trimmedFullName }));
        setAccountPassword("");
        setAccountConfirmPassword("");
        setAccountSuccessOpen(true);
        toast({ title: t("Saved"), description: t("Account settings updated.") });
      } else {
        const message = data?.message || t("Failed to save account settings.");
        setError(message);
        toast({
          title: t("Save failed"),
          description: message,
          variant: "destructive",
        });
      }
    } catch (e) {
      const message = e.message || t("Failed to save account settings.");
      setError(message);
      toast({
        title: t("Save failed"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {loading && <FullScreenLoader />}
      <Dialog open={accountSuccessOpen} onOpenChange={setAccountSuccessOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="font-display">
              {t("Account Settings Updated")}
            </DialogTitle>
            <DialogDescription>
              {t("Your account details have been saved successfully.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setAccountSuccessOpen(false)}>
              {t("Done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("Pause Subscription")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "Please contact west@gowest.ai to pause your account and payments.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="space-x-2">
            <Button
              variant="secondary"
              onClick={() => setCancelDialogOpen(false)}
            >
              {t("Close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="max-w-5xl mx-auto space-y-6 py-2">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("Settings")}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("settings.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Account Settings */}
          <Card className="shadow-[var(--card-shadow)]">
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-accent" />
                <CardTitle className="text-lg font-display">
                  {t("Account Settings")}
                </CardTitle>
              </div>
              <CardDescription>
                {t("Update your contact information and password.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="accountFullName">{t("Full Name")}</Label>
                  <Input
                    id="accountFullName"
                    value={accountFullName}
                    onChange={(e) => setAccountFullName(e.target.value)}
                    placeholder={t("Enter your full name")}
                    autoComplete="name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountPhone">{t("Phone")}</Label>
                  <Input
                    id="accountPhone"
                    type="tel"
                    value={accountPhone}
                    onChange={(e) => setAccountPhone(e.target.value)}
                    placeholder={t("Enter your phone number")}
                    autoComplete="tel"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="accountPassword">{t("New Password")}</Label>
                  <Input
                    id="accountPassword"
                    type="password"
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                    placeholder={t("Leave blank to keep current")}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountConfirmPassword">
                    {t("Confirm Password")}
                  </Label>
                  <Input
                    id="accountConfirmPassword"
                    type="password"
                    value={accountConfirmPassword}
                    onChange={(e) => setAccountConfirmPassword(e.target.value)}
                    placeholder={t("Confirm new password")}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          {/* White Label */}
          <Card className="shadow-[var(--card-shadow)]">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-accent" />
                <CardTitle className="text-lg font-display">
                  {t("White Label")}
                </CardTitle>
              </div>
              <CardDescription>
                {t(
                  "Your company name and logo appear on all generated documents.",
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="companyName">{t("form.companyName")}</Label>
                <Input
                  id="companyName"
                  placeholder={t("Enter your company name")}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t("Company Logo")}</Label>
                {logoPreview ? (
                  <div className="flex items-center gap-4 relative">
                    <button
                      onClick={removeLogo}
                      className="absolute -top-1.5 z-20 -left-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 hover:bg-destructive/90 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="relative h-20 w-20 rounded-lg border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                      <img
                        src={logoPreview}
                        alt={t("Company Logo")}
                        className="h-full w-full object-contain p-1"
                      />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground">
                        {logoFile?.name}
                      </p>
                      <p>
                        {logoFile
                          ? (logoFile.size / 1024).toFixed(1) + " KB"
                          : ""}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={handleUploadLogo}
                    className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary/50 hover:bg-primary/10 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Upload className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {t("Click to upload logo")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("PNG, JPG, SVG up to 2MB")}
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>

              {(companyName || logoPreview) && (
                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider font-medium">
                    {t("Document Header Preview")}
                  </p>
                  <div className="flex items-center gap-3 bg-background rounded-md p-3 border border-border">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt={t("Company Logo")}
                        className="h-8 w-8 object-contain"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                        <Image className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <span className="font-semibold text-foreground">
                      {companyName || t("Your Company Name")}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Language */}
          <Card className="shadow-[var(--card-shadow)]">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-accent" />
                <CardTitle className="text-lg font-display">
                  {t("Language")}
                </CardTitle>
              </div>
              <CardDescription>
                {t(
                  "Select the language for generated output documents and reports.",
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="language">{t("Output Language")}</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("Select language")} />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "This determines the language used in AI Readiness Assessments and Policy documents.",
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Subscription */}
          <Card className="shadow-[var(--card-shadow)]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-accent" />
                <CardTitle className="text-lg font-display">
                  {t("Subscription")}
                </CardTitle>
              </div>
              <Badge variant="secondary" className="text-xs font-semibold">
                {tier}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t(details.assessments)} · {t("Billed quarterly at")}{" "}
                    {details.quarterly === "Custom"
                      ? t("Custom")
                      : details.quarterly}
                  </p>
                </div>
                {String(
                  localSubscriptionStatus || subscriptionStatus || "",
                ).toLowerCase() === "active" && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-10 w-full sm:w-auto"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    {t("Pause Subscription")}
                  </Button>
                )}
              </div>

              <Separator />

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {t("Quarterly Invoices")}
                  </span>
                </div>
                <div className="space-y-2">
                  {invoiceLoading ? (
                    <div className="rounded-md border px-3 py-4 text-sm text-muted-foreground">
                      {t("Loading upcoming invoices...")}
                    </div>
                  ) : subscriptionInvoices.length > 0 ? (
                    subscriptionInvoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between rounded-md border px-3 py-2"
                      >
                        <div>
                          <p className="text-sm font-medium">{inv.id}</p>
                          <p className="text-xs text-muted-foreground">
                            {inv.description}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatInvoiceDate(inv.date)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">
                            {formatInvoiceAmount(inv.amount, inv.currency)}
                          </span>
                          <Badge
                            variant={
                              inv.status === "paid" ? "secondary" : "default"
                            }
                            className="text-[10px]"
                          >
                            {inv.status === "paid" ? t("Paid") : t("Upcoming")}
                          </Badge>
                          {inv.invoiceUrl && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() =>
                                window.open(inv.invoiceUrl, "_blank")
                              }
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-md border px-3 py-4 text-sm text-muted-foreground">
                      {t("No upcoming invoices found.")}
                    </div>
                  )}
                </div>
              </div>

              {showUpgrade && (
                <>
                  <Separator />
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold">
                          {t("Upgrade to Pro")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("settings.upgradeCopy")}
                        </p>
                        <Button size="sm" className="text-xs h-8 mt-3">
                          {t("Upgrade to Pro")}{" "}
                          <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Stripe Billing */}
          <Card className="shadow-[var(--card-shadow)]">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-accent" />
                <CardTitle className="text-lg font-display">
                  {t("Billing")}
                </CardTitle>
              </div>
              <CardDescription>
                {t("Manage your payment method and billing details.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Payment Method */}
              {/* <div className="space-y-2">
                <Label>Payment Method</Label>
                <div className="flex items-center justify-between rounded-md border px-3 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-12 rounded bg-muted flex items-center justify-center">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">•••• •••• •••• 4242</p>
                      <p className="text-xs text-muted-foreground">
                        Expires 12/2027
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs">
                    Update
                  </Button>
                </div>
              </div> */}

              <Separator />

              {/* Billing Info */}
              <div className="space-y-3">
                <Label>{t("Billing Details")}</Label>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("Name")}</span>
                    <span className="font-medium">
                      {billingInfo.name || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("Email")}</span>
                    <span className="font-medium">
                      {billingInfo.email || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("Plan")}</span>
                    <span className="font-medium">
                      {billingInfo.plan || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("Next billing")}
                    </span>
                    <span className="font-medium">
                      {billingInfo.nextBilling || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("Amount")}</span>
                    <span className="font-medium">
                      {billingInfo.amount || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <Separator />

              <Button
                variant="outline"
                className="w-full gap-2 text-sm"
                onClick={() => navigate("/billing")}
              >
                <ExternalLink className="h-4 w-4" />
                {t("Open Billing Portal")}
              </Button>
            </CardContent>
          </Card>
        </div>

        {isAdminUser && (
          <DiscountProvider>
            <AdminDiscountPanel />
          </DiscountProvider>
        )}
        {/* Save */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleSaveAccount}
            disabled={loading}
            className="px-6"
          >
            {loading ? t("Saving...") : t("Save Settings")}
          </Button>
        </div>
      </div>
      <Toaster />
    </DashboardLayout>
  );
};

export default SettingsNewV1;
